<template>
  <section>
    <div class="listview__head">
      <div>
        <div class="c-lbl">Gastos propios</div>
        <div class="listview__desc">
          Finanzas personales · Reducción inteligente de gastos
        </div>
      </div>
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
        <button class="btn btn-g" @click="catMgr.open = true">Categorías</button>
        <button class="btn btn-g" @click="onImportarPdf">+ Importar PDF / Excel</button>
        <button class="btn btn-p" @click="openNew">+ Cargar manual</button>
      </div>
    </div>

    <!-- Saldo bancario actual (suma de los saldos finales de cada cuenta) -->
    <div v-if="saldos.total_saldo" class="card" style="margin-bottom:12px;padding:16px;background:linear-gradient(135deg,#032B52 0%,#1560BD 100%);color:#fff;border:none">
      <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px">
        <div>
          <div style="font-size:11px;font-weight:600;letter-spacing:1px;text-transform:uppercase;color:#bfdbfe">Saldo bancario actual</div>
          <div style="font-size:28px;font-weight:700;margin-top:4px">{{ fmt(saldos.total_saldo) }}</div>
          <div class="xs" style="margin-top:4px;color:#bfdbfe;opacity:0.9">Suma de los saldos finales reportados por cada cuenta</div>
        </div>
        <div style="display:flex;gap:24px;flex-wrap:wrap">
          <div v-for="c in saldos.por_cuenta" :key="c.numero_cuenta" style="text-align:right">
            <div class="xs" style="color:#bfdbfe;font-weight:600">Cuenta {{ c.numero_cuenta }}</div>
            <div style="font-size:17px;font-weight:600;margin-top:2px">{{ fmt(c.saldo) }}</div>
            <div class="xs" style="color:#bfdbfe;opacity:0.8;margin-top:2px">al {{ fmtDate(c.fecha) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Resumen del período — Noelia + Mauricio como una unidad económica.
         Los movs internos (transfers Noelia↔Mauricio, USD, TC propia) no se contabilizan. -->
    <div class="card" style="margin-bottom:12px;padding:14px">
      <div class="form-row form-row--4">
        <div>
          <div class="c-lbl">Gastos</div>
          <div class="grad-text" style="font-size:22px;font-weight:700">{{ fmt(resumen.total_gastos) }}</div>
          <div class="xs" style="margin-top:4px">Salidas reales (todo lo que gastaste)</div>
        </div>
        <div>
          <div class="c-lbl">Ingresos por facturación</div>
          <div style="font-size:22px;font-weight:700;color:#16a34a">{{ fmt(resumen.total_ingresos_genuinos) }}</div>
          <div class="xs" style="margin-top:4px">Cobros de clientes (Noelia + Mauricio)</div>
        </div>
        <div>
          <div class="c-lbl">Depósitos en efectivo</div>
          <div style="font-size:22px;font-weight:700;color:#9ca3af">{{ fmt(resumen.total_depositos_propios) }}</div>
          <div class="xs" style="margin-top:4px">Ahorros que usaste · cuánta plata no vino por facturación</div>
        </div>
        <div>
          <div class="c-lbl">Flujo del período</div>
          <div style="font-size:22px;font-weight:700"
               :style="{ color: flujoPeriodo >= 0 ? '#16a34a' : '#c0392b' }">
            {{ flujoPeriodo >= 0 ? '+' : '' }}{{ fmt(flujoPeriodo) }}
          </div>
          <div class="xs" style="margin-top:4px">
            I + Dep − G · cambio neto del saldo en el período
          </div>
          <div class="xs" style="margin-top:6px;color:#6b7280;line-height:1.4">
            Saldo al inicio: <strong>{{ fmt(saldoInicioPeriodo) }}</strong><br>
            Saldo al cierre: <strong>{{ fmt(saldos.total_saldo || 0) }}</strong>
          </div>
        </div>
      </div>
    </div>

    <!-- Filtros -->
    <div class="listview__filters">
      <input class="fi" v-model="filters.desde" type="date" @change="fetch" />
      <input class="fi" v-model="filters.hasta" type="date" @change="fetch" />
      <select class="fi" v-model="filters.tipo" @change="fetch" style="max-width:160px">
        <option value="">Todos los tipos</option>
        <option value="gasto">Gastos</option>
        <option value="ingreso">Ingresos</option>
        <option value="deposito_propio">Depósitos propios</option>
      </select>
      <select class="fi" v-model="filters.categoria_id" @change="fetch" style="max-width:200px">
        <option value="">Todas las categorías</option>
        <option v-for="c in categorias" :key="c.id" :value="c.id">{{ c.nombre }}</option>
      </select>
      <label class="fx-ac" style="gap:6px;font-size:12.5px;color:var(--tx-md);cursor:pointer">
        <input type="checkbox" v-model="filters.ineludible" @change="fetch" /> Solo ineludibles
      </label>
      <input class="fi" v-model="filters.q" @input="debouncedFetch" placeholder="Buscar descripción..." />
    </div>

    <!-- Lista (desktop: tabla con edición inline · mobile: tarjetas) -->
    <div class="card">
      <!-- Desktop: tabla con selects inline -->
      <div class="gastos-tabla tbl-wrap">
        <table class="tbl">
          <thead>
            <tr>
              <th>Fecha</th><th>Descripción</th>
              <th style="min-width:180px">Categoría</th>
              <th>Tipo</th>
              <th style="text-align:center">Ineludible</th>
              <th style="text-align:right">Monto</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading"><td colspan="7" class="tbl-empty">Cargando...</td></tr>
            <tr v-else-if="!items.length"><td colspan="7" class="tbl-empty">
              Sin movimientos todavía. Subí un resumen bancario o cargá uno manual.
            </td></tr>
            <tr v-for="m in items" :key="m.id">
              <td class="mono">{{ fmtDate(m.fecha) }}</td>
              <td>{{ m.descripcion }}</td>
              <td>
                <select class="fi inline-select"
                        :value="m.categoria_id || ''"
                        @change="onCategoriaInline(m, $event.target.value)"
                        :style="m.categoria ? { background: m.categoria.color + '15', borderColor: m.categoria.color + '55' } : {}">
                  <option value="">— sin clasificar —</option>
                  <optgroup v-for="g in categoriasGroup(m.tipo)" :key="g.label" :label="g.label">
                    <option v-for="c in g.items" :key="c.id" :value="c.id">{{ c.nombre }}</option>
                  </optgroup>
                  <option value="__new__">+ Crear nueva categoría…</option>
                </select>
              </td>
              <td>
                <span class="pill" :class="tipoPill(m.tipo)">{{ tipoLabel(m.tipo) }}</span>
              </td>
              <td style="text-align:center">
                <label style="display:inline-flex;align-items:center;cursor:pointer">
                  <input type="checkbox" :checked="!!m.ineludible"
                         @change="onIneludibleInline(m, $event.target.checked)"
                         style="width:18px;height:18px;cursor:pointer" />
                </label>
              </td>
              <td class="mono" style="text-align:right">
                <strong :style="{ color: m.tipo === 'gasto' ? '#c0392b' : (m.tipo === 'ingreso' ? '#16a34a' : '#6b7280') }">
                  {{ fmt(m.monto) }}
                </strong>
              </td>
              <td>
                <button class="btn-ico" @click="openEdit(m)" title="Editar todo">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile: tarjetas -->
      <div class="gastos-cards">
        <div v-if="loading" class="tbl-empty">Cargando...</div>
        <div v-else-if="!items.length" class="tbl-empty">
          Sin movimientos todavía. Subí un resumen bancario o cargá uno manual.
        </div>
        <div v-for="m in items" :key="'c-' + m.id" class="gasto-card">
          <div class="gasto-card__head">
            <div>
              <div class="mono xs">{{ fmtDate(m.fecha) }}</div>
              <div style="font-weight:600;margin-top:2px">{{ m.descripcion }}</div>
            </div>
            <strong class="mono" :style="{ color: m.tipo === 'gasto' ? '#c0392b' : (m.tipo === 'ingreso' ? '#16a34a' : '#6b7280') }">
              {{ fmt(m.monto) }}
            </strong>
          </div>
          <div class="gasto-card__row">
            <span class="pill" :class="tipoPill(m.tipo)">{{ tipoLabel(m.tipo) }}</span>
            <select class="fi inline-select"
                    :value="m.categoria_id || ''"
                    @change="onCategoriaInline(m, $event.target.value)"
                    :style="m.categoria ? { background: m.categoria.color + '15', borderColor: m.categoria.color + '55' } : {}">
              <option value="">— sin clasificar —</option>
              <optgroup v-for="g in categoriasGroup(m.tipo)" :key="g.label" :label="g.label">
                <option v-for="c in g.items" :key="c.id" :value="c.id">{{ c.nombre }}</option>
              </optgroup>
              <option value="__new__">+ Nueva…</option>
            </select>
          </div>
          <div class="gasto-card__row">
            <label style="display:inline-flex;align-items:center;gap:6px;cursor:pointer;font-size:13px">
              <input type="checkbox" :checked="!!m.ineludible"
                     @change="onIneludibleInline(m, $event.target.checked)"
                     style="width:18px;height:18px;cursor:pointer" />
              Ineludible
            </label>
            <button class="btn btn-g btn-sm" @click="openEdit(m)">Editar</button>
          </div>
        </div>
      </div>

      <div class="listview__pag" v-if="pagination.last_page > 1">
        <button class="btn btn-g btn-sm" :disabled="pagination.current_page <= 1" @click="go(pagination.current_page - 1)">‹ Anterior</button>
        <span>Página {{ pagination.current_page }} / {{ pagination.last_page }}</span>
        <button class="btn btn-g btn-sm" :disabled="pagination.current_page >= pagination.last_page" @click="go(pagination.current_page + 1)">Siguiente ›</button>
      </div>
    </div>

    <!-- Modal Importar -->
    <Modal v-model="importar.open" :title="'Importar extracto bancario'" :max-width="900">
      <!-- Paso 1: upload -->
      <div v-if="importar.paso === 'upload'">
        <p style="font-size:13px;color:var(--tx-md);margin-bottom:14px">
          Subí un extracto Galicia: <strong>.xlsx</strong> del online banking (caja de ahorro pesos)
          o <strong>.pdf</strong> (resúmenes de cuenta o tarjeta de crédito).
          Los movimientos van a procesarse y clasificarse automáticamente. Vas a poder revisarlos
          antes de guardarlos.
        </p>
        <input type="file" accept=".xlsx,.xls,.pdf" @change="onFileSelected" class="fi" />
        <div v-if="importar.error" class="form-error" style="margin-top:10px">{{ importar.error }}</div>
      </div>

      <!-- Paso 2: preview -->
      <div v-else-if="importar.paso === 'preview'">
        <div style="font-size:13px;margin-bottom:10px">
          <strong>{{ importar.filename }}</strong> · {{ importar.movs.length }} movimientos detectados ·
          de <strong>{{ fmtDate(importar.fechaDesde) }}</strong> a <strong>{{ fmtDate(importar.fechaHasta) }}</strong>
          <span v-if="importar.cuenta"> · Cuenta <strong>{{ importar.cuenta }}</strong></span>
        </div>
        <div v-if="importar.skipInternos > 0" style="font-size:12px;color:#1560BD;margin-bottom:6px">
          <strong>{{ importar.skipInternos }}</strong> movimientos internos descartados (transferencias Noelia↔Mauricio, CUIT propio, USD, pagos TC).
        </div>

        <!-- Bloque de saldo detectado con confirmación explícita -->
        <div v-if="importar.saldoFinal != null"
             style="background:#f0f7ff;border:1px solid #c7dffd;border-radius:6px;padding:10px 12px;margin-bottom:10px">
          <label style="display:flex;align-items:center;gap:10px;cursor:pointer;font-size:13px">
            <input type="checkbox" v-model="importar.aplicarSaldo"
                   style="width:18px;height:18px;cursor:pointer;flex-shrink:0" />
            <div style="flex:1">
              <div>
                Aplicar el <strong>saldo final detectado</strong> a la cuenta
                <span v-if="importar.cuenta"><strong>{{ importar.cuenta }}</strong>: </span>
                <span style="font-size:15px;font-weight:700;color:#032B52">{{ fmt(importar.saldoFinal) }}</span>
              </div>
              <div class="xs" style="margin-top:4px;color:var(--tx-md)">
                Si lo destildás, los movimientos se guardan pero el saldo de la cuenta queda como estaba antes.
                Útil si sospechás que el parser detectó mal o si el archivo es histórico.
              </div>
            </div>
          </label>
        </div>
        <div v-else style="font-size:12px;color:#c0392b;margin-bottom:8px">
          ⚠ El parser no detectó saldo final en este archivo. Solo se van a guardar los movimientos.
        </div>

        <div style="font-size:12px;color:var(--tx-md);margin-bottom:8px">
          Movimientos duplicados con lo que ya tenés en la base van a ignorarse al guardar.
        </div>
        <div style="max-height:380px;overflow-y:auto;border:1px solid var(--brd,#e2e8f0);border-radius:6px">
          <table class="tbl" style="font-size:12.5px">
            <thead style="position:sticky;top:0;background:#f8fafc;z-index:1">
              <tr><th>Fecha</th><th>Descripción</th><th>Tipo</th><th style="text-align:right">Monto</th></tr>
            </thead>
            <tbody>
              <tr v-for="(m, i) in importar.movs" :key="i">
                <td class="mono" style="white-space:nowrap">{{ fmtDate(m.fecha) }}</td>
                <td>{{ m.descripcion }}</td>
                <td>
                  <select class="fi inline-select" v-model="m.tipo" style="font-size:12px">
                    <option value="gasto">Gasto</option>
                    <option value="ingreso">Ingreso</option>
                    <option value="deposito_propio">Depósito propio</option>
                  </select>
                </td>
                <td class="mono" style="text-align:right">{{ fmt(m.monto) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="importar.error" class="form-error" style="margin-top:10px">{{ importar.error }}</div>
      </div>

      <!-- Paso 3: guardando -->
      <div v-else-if="importar.paso === 'saving'">
        Guardando {{ importar.movs.length }} movimientos…
      </div>

      <!-- Paso 4: hecho -->
      <div v-else-if="importar.paso === 'done'">
        <p style="font-size:15px;color:#16a34a;margin:0 0 10px">✓ Importación completada</p>
        <div style="font-size:13px">
          Movimientos insertados: <strong>{{ importar.resultado.insertados }}</strong><br>
          Duplicados ignorados: <strong>{{ importar.resultado.duplicados }}</strong>
        </div>
      </div>

      <template #footer>
        <button v-if="importar.paso === 'upload'" class="btn btn-g" @click="importar.open = false">Cancelar</button>
        <button v-if="importar.paso === 'preview'" class="btn btn-g" @click="importar.paso = 'upload'">← Volver</button>
        <button v-if="importar.paso === 'preview'" class="btn btn-p" @click="confirmarImportacion">Confirmar e importar ({{ importar.movs.length }})</button>
        <button v-if="importar.paso === 'done'" class="btn btn-p" @click="importar.open = false">Listo</button>
      </template>
    </Modal>

    <!-- Modal Administrar categorías -->
    <Modal v-model="catMgr.open" title="Administrar categorías" :max-width="640">
      <div style="font-size:12px;color:var(--tx-md);margin-bottom:10px">
        Editá el nombre o color de cada categoría. Las del sistema (★) no se pueden eliminar pero sí renombrar.
      </div>
      <div v-for="grupo in catEditGroups" :key="grupo.tipo" style="margin-bottom:16px">
        <div style="font-size:11px;font-weight:700;letter-spacing:1px;color:var(--tx-md);text-transform:uppercase;margin-bottom:6px">
          {{ grupo.label }}
        </div>
        <div v-for="c in grupo.items" :key="c.id"
             style="display:grid;grid-template-columns:42px 1fr auto 32px;gap:8px;align-items:center;padding:6px 0;border-bottom:1px solid #eef2f7">
          <input type="color" :value="c.color" @change="onColorCat(c, $event.target.value)"
                 style="width:36px;height:32px;padding:0;border:1px solid #ddd;border-radius:4px;cursor:pointer" />
          <input class="fi" :value="c.nombre" @change="onNombreCat(c, $event.target.value)"
                 style="height:32px;font-size:13px" />
          <span class="xs" style="opacity:0.7">{{ c.es_sistema ? '★ sistema' : '' }}</span>
          <button v-if="!c.es_sistema" class="btn-ico" @click="eliminarCat(c)" title="Eliminar"
                  style="color:#c33;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
          </button>
          <span v-else></span>
        </div>
      </div>
      <button class="btn btn-p btn-sm" @click="abrirNuevaCategoria" style="margin-top:8px">+ Nueva categoría</button>
      <template #footer>
        <button class="btn btn-g" @click="catMgr.open = false">Cerrar</button>
      </template>
    </Modal>

    <!-- Modal Nueva categoría rápida -->
    <Modal v-model="newCat.open" title="Nueva categoría" :max-width="420">
      <form @submit.prevent="crearCategoriaRapida" id="newcat-form" class="form-grid">
        <div>
          <label class="fl">Nombre *</label>
          <input class="fi" v-model="newCat.nombre" maxlength="80" required autofocus />
        </div>
        <div class="form-row form-row--2">
          <div>
            <label class="fl">Tipo *</label>
            <select class="fi" v-model="newCat.tipo" required>
              <option value="gasto">Gasto</option>
              <option value="ingreso">Ingreso</option>
              <option value="transferencia">Transferencia</option>
            </select>
          </div>
          <div>
            <label class="fl">Color</label>
            <input class="fi" type="color" v-model="newCat.color" />
          </div>
        </div>
        <div v-if="newCat.error" class="form-error">{{ newCat.error }}</div>
      </form>
      <template #footer>
        <button class="btn btn-g" @click="newCat.open = false">Cancelar</button>
        <button class="btn btn-p" form="newcat-form" type="submit" :disabled="newCat.saving">
          {{ newCat.saving ? 'Creando...' : 'Crear' }}
        </button>
      </template>
    </Modal>

    <!-- Modal crear / editar -->
    <Modal v-model="modal.open" :title="modal.id ? 'Editar movimiento' : 'Nuevo movimiento'" :max-width="520">
      <form @submit.prevent="save" id="mov-form" class="form-grid">
        <div class="form-row form-row--2">
          <div>
            <label class="fl">Fecha *</label>
            <input class="fi" type="date" v-model="modal.data.fecha" required />
          </div>
          <div>
            <label class="fl">Monto *</label>
            <input class="fi" type="number" step="0.01" v-model.number="modal.data.monto" required />
          </div>
        </div>
        <div>
          <label class="fl">Descripción *</label>
          <input class="fi" v-model="modal.data.descripcion" maxlength="300" required />
        </div>
        <div class="form-row form-row--2">
          <div>
            <label class="fl">Tipo *</label>
            <select class="fi" v-model="modal.data.tipo" required>
              <option value="gasto">Gasto</option>
              <option value="ingreso">Ingreso</option>
              <option value="deposito_propio">Depósito propio</option>
            </select>
          </div>
          <div>
            <label class="fl">Categoría</label>
            <select class="fi" v-model="modal.data.categoria_id">
              <option :value="null">— sin clasificar —</option>
              <option v-for="c in categoriasFiltradas" :key="c.id" :value="c.id">{{ c.nombre }}</option>
            </select>
          </div>
        </div>
        <label style="display:flex;align-items:center;gap:10px;font-size:13px;cursor:pointer;padding-top:4px">
          <input type="checkbox" v-model="modal.data.ineludible" style="flex-shrink:0;width:16px;height:16px;cursor:pointer" />
          <span>Ineludible (no se puede eliminar este gasto)</span>
        </label>
        <div v-if="modal.error" class="form-error">{{ modal.error }}</div>
      </form>
      <template #footer>
        <button v-if="modal.id" class="btn btn-g btn-sm" @click="del" style="margin-right:auto;color:#c33;">Eliminar</button>
        <button class="btn btn-g" @click="modal.open = false">Cancelar</button>
        <button class="btn btn-p" form="mov-form" type="submit" :disabled="modal.saving">
          {{ modal.saving ? 'Guardando...' : (modal.id ? 'Actualizar' : 'Crear') }}
        </button>
      </template>
    </Modal>
  </section>
</template>

<script>
import api from '../services/api';
import Modal from '../components/Modal.vue';
import * as XLSX from 'xlsx';

const emptyMov = () => ({
  fecha: new Date().toLocaleDateString('en-CA'),
  descripcion: '',
  monto: 0,
  tipo: 'gasto',
  categoria_id: null,
  ineludible: false,
});

// Helpers usados por el fallback de parsing client-side cuando el backend
// nuevo todavía no está deployado (DO autodeploy puede tardar).
function parseMontoAr(s) {
  if (!s) return 0;
  const n = String(s).trim().replace(/\./g, '').replace(',', '.');
  const v = parseFloat(n);
  return Number.isNaN(v) ? 0 : v;
}
function fechaArToIso(fechaAr) {
  const [d, m, y] = fechaAr.split('/');
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}
function sugerirTipoCliente(desc, monto) {
  const d = (desc || '').toLowerCase();
  if (d.includes('dep.efvo') || d.includes('deposito en efectivo')) return 'deposito_propio';
  return monto < 0 ? 'gasto' : 'ingreso';
}

// Patrones de movimientos internos (Noelia↔Mauricio, CUIT propio, transf entre cuentas
// propias, compra/venta de USD, pagos de TC propias). Por regla del usuario "no se
// contabilizan" — se filtran de los totales del resumen y del preview del import.
// Los CUITs usan word boundary para evitar falsos positivos con números de referencia
// que contienen la misma secuencia (ej. "202162605470" en MONOTR FISICA).
const CUIT_INTERNOS_RE = /(^|[^0-9])(20216260547|23252267484)([^0-9]|$)/;
function esMovInterno(desc) {
  const d = (desc || '').toLowerCase();
  const substrings = [
    'noelia celina gonzal',
    'transf. ctas propias', 'transferencia de cuenta',
    'compra venta de dolares', 'compra moneda extran', 'venta moneda extran',
    'pago tarjeta visa', 'pago tarjeta master',
  ];
  if (substrings.some(p => d.includes(p))) return true;
  if (CUIT_INTERNOS_RE.test(d)) return true;
  if (d.includes('pago de servicios') && d.includes('amex')) return true;
  return false;
}

// Default: últimos 30 días corridos (incluye hoy).
const hace30dias = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toLocaleDateString('en-CA');
};
const hoy = () => new Date().toLocaleDateString('en-CA');

export default {
  name: 'GastosView',
  components: { Modal },
  data() {
    return {
      items: [],
      categorias: [],
      resumen: {
        total_gastos: 0, total_gastos_ineludibles: 0, total_gastos_eliminables: 0,
        total_ingresos_genuinos: 0, total_depositos_propios: 0, consumo_ahorros: 0,
      },
      saldos: { total_saldo: 0, por_cuenta: [] },
      loading: false,
      pagination: { current_page: 1, last_page: 1, total: 0 },
      filters: { desde: hace30dias(), hasta: hoy(), tipo: '', categoria_id: '', ineludible: false, q: '', page: 1 },
      modal: { open: false, id: null, saving: false, error: '', data: emptyMov() },
      newCat: { open: false, nombre: '', tipo: 'gasto', color: '#6b7280', saving: false, error: '', pendingMov: null },
      catMgr: { open: false },
      importar: {
        open: false,
        paso: 'upload',            // upload | preview | saving | done
        filename: '',
        cuenta: '',
        saldoInicial: null,
        saldoFinal: null,
        aplicarSaldo: true,        // checkbox: si el owner confirma que el saldo detectado se aplica
        fechaDesde: '',
        fechaHasta: '',
        movs: [],                  // array de { fecha, descripcion, monto, tipo, raw_text }
        skipInternos: 0,           // movs internos descartados (Noelia, CUIT propio, etc.)
        error: '',
        resultado: { insertados: 0, duplicados: 0 },
      },
      _debounce: null,
    };
  },
  computed: {
    categoriasFiltradas() {
      const t = this.modal.data.tipo;
      const map = { gasto: 'gasto', ingreso: 'ingreso', deposito_propio: 'transferencia' };
      return this.categorias.filter(c => c.tipo === map[t]);
    },
    // Agrupar categorías por tipo. Para usar en el <select> inline,
    // priorizando el tipo del movimiento pero mostrando todas.
    categoriasPorTipo() {
      const por = { gasto: [], ingreso: [], transferencia: [] };
      this.categorias.forEach(c => {
        if (por[c.tipo]) por[c.tipo].push(c);
      });
      return por;
    },
    catEditGroups() {
      const labels = { gasto: 'Gastos', ingreso: 'Ingresos', transferencia: 'Transferencias' };
      return ['gasto', 'ingreso', 'transferencia']
        .map(t => ({ tipo: t, label: labels[t], items: this.categoriasPorTipo[t] || [] }))
        .filter(g => g.items.length > 0);
    },
    // Flujo "contabilizable" del período = Ingresos + Depósitos efectivo − Gastos
    // (gastos llega negativo del backend, sumamos los tres).
    flujoPeriodo() {
      const g = Number(this.resumen.total_gastos || 0);            // negativo
      const i = Number(this.resumen.total_ingresos_genuinos || 0); // positivo
      const d = Number(this.resumen.total_depositos_propios || 0); // positivo
      return g + i + d;
    },
    // Saldo al inicio del período. La math correcta usa el flujo BANCARIO real
    // (incluyendo movs internos como Noelia↔Mauricio, USD, pagos de TC propias),
    // no solo el flujo "contabilizable" que muestran los 3 cards. Sin eso, la
    // diferencia entre saldo actual y los cards no cuadra con la realidad.
    saldoInicioPeriodo() {
      const flujoReal = Number(
        this.resumen.flujo_bancario_total != null
          ? this.resumen.flujo_bancario_total
          : this.flujoPeriodo
      );
      return Number(this.saldos.total_saldo || 0) - flujoReal;
    },
  },
  created() {
    this.fetchCategorias();
    this.fetch();
    this.fetchResumen();
    this.fetchSaldos();
  },
  methods: {
    async fetchCategorias() {
      try {
        const { data } = await api.get('/gastos/categorias');
        this.categorias = data;
      } catch (e) { /* noop */ }
    },
    async fetch() {
      this.loading = true;
      try {
        const params = { page: this.filters.page };
        if (this.filters.desde) params.desde = this.filters.desde;
        if (this.filters.hasta) params.hasta = this.filters.hasta;
        if (this.filters.tipo)  params.tipo  = this.filters.tipo;
        if (this.filters.q)     params.q     = this.filters.q;
        if (this.filters.ineludible) params.ineludible = 1;
        // categoria_id: forzar a número si viene como string "27", o no enviar si vacío
        if (this.filters.categoria_id !== '' && this.filters.categoria_id != null) {
          const cid = Number(this.filters.categoria_id);
          if (!Number.isNaN(cid) && cid > 0) params.categoria_id = cid;
        }

        // eslint-disable-next-line no-console
        console.log('[Gastos] fetch params →', params);

        const { data } = await api.get('/gastos/movimientos', { params });
        // eslint-disable-next-line no-console
        console.log('[Gastos] fetch response →', { total: data.total, data_length: (data.data || []).length });

        this.items = data.data || [];
        this.pagination = {
          current_page: data.current_page || 1,
          last_page:    data.last_page || 1,
          total:        data.total || 0,
        };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[Gastos] fetch ERROR →', e?.response?.status, e?.response?.data, e);
        this.items = [];
      } finally { this.loading = false; }
      // El resumen también debe respetar el filtro de fechas/tipo/categoría
      this.fetchResumen();
    },
    async fetchSaldos() {
      // Workaround: el endpoint /gastos/saldos requiere deploy del backend.
      // Mientras tanto leemos /gastos/importaciones (ya deployado) y calculamos
      // localmente los saldos por cuenta. Cuando el endpoint nuevo esté arriba,
      // este método se simplifica volviendo al fetch directo.
      try {
        const { data } = await api.get('/gastos/importaciones', { params: { per_page: 200 } });
        const filas = Array.isArray(data) ? data : (data.data || []);

        // Por cada numero_cuenta tomar la importación más reciente (mayor fecha_hasta).
        const porCuenta = {};
        for (const imp of filas) {
          if (!imp.numero_cuenta || imp.saldo_final == null) continue;
          const prev = porCuenta[imp.numero_cuenta];
          if (!prev || imp.fecha_hasta > prev.fecha_hasta) {
            porCuenta[imp.numero_cuenta] = imp;
          }
        }

        const detalle = Object.values(porCuenta).map(imp => ({
          numero_cuenta: imp.numero_cuenta,
          banco:         imp.banco,
          cuenta_tipo:   imp.cuenta_tipo,
          fecha:         imp.fecha_hasta,
          saldo:         Number(imp.saldo_final),
        }));
        const total = detalle.reduce((s, c) => s + c.saldo, 0);

        this.saldos = { total_saldo: total, por_cuenta: detalle };
      } catch (e) { /* noop */ }
    },
    async fetchResumen() {
      // Computa el resumen client-side iterando TODAS las páginas paginadas.
      // No depende del backend respetando per_page (que estaba hardcoded a 50
      // hasta el fix 8e16ed9 y puede que ese deploy no haya corrido todavía).
      // Loop garantizado a terminar: max 200 páginas como red de seguridad.
      try {
        const baseParams = {};
        ['desde', 'hasta', 'categoria_id', 'q'].forEach(k => {
          if (this.filters[k]) baseParams[k] = this.filters[k];
        });
        if (this.filters.ineludible) baseParams.ineludible = 1;

        const allMovs = [];
        let page = 1;
        let lastPage = 1;
        const MAX_PAGES = 200;
        do {
          const { data } = await api.get('/gastos/movimientos', { params: { ...baseParams, page } });
          const movs = (data && data.data) ? data.data : (Array.isArray(data) ? data : []);
          allMovs.push(...movs);
          lastPage = (data && data.last_page) ? Number(data.last_page) : 1;
          page++;
        } while (page <= lastPage && page <= MAX_PAGES);

        let totalGastos = 0, totalGastosInel = 0, totalGastosElim = 0;
        let totalIngresos = 0, totalDepositos = 0;
        let depositosNegFiltrados = 0; // negativos de deposito_propio que clamean a 0 pero SÍ tocan el banco
        let flujoBancarioTotal = 0;    // sum de TODOS los movs (incluso internos): toca cuenta = entra al saldo
        for (const m of allMovs) {
          const monto = Number(m.monto || 0);
          flujoBancarioTotal += monto;

          // Filtro: descarta movs internos (Noelia↔Mauricio, CUIT propio, USD, TC propias).
          if (esMovInterno(m.descripcion || '')) continue;
          if (m.tipo === 'gasto') {
            totalGastos += monto;
            if (m.ineludible) totalGastosInel += monto;
            else              totalGastosElim += monto;
          } else if (m.tipo === 'ingreso') {
            totalIngresos += monto;
          } else if (m.tipo === 'deposito_propio') {
            // Solo positivos en el card. Los negativos residuales se acumulan
            // aparte pero NO se restan del card "Depósitos en efectivo".
            if (monto > 0) totalDepositos += monto;
            else           depositosNegFiltrados += monto;
          }
        }
        this.resumen = {
          total_gastos:              Math.round(totalGastos * 100) / 100,
          total_gastos_ineludibles:  Math.round(totalGastosInel * 100) / 100,
          total_gastos_eliminables:  Math.round(totalGastosElim * 100) / 100,
          total_ingresos_genuinos:   Math.round(totalIngresos * 100) / 100,
          total_depositos_propios:   Math.round(totalDepositos * 100) / 100,
          depositos_neg_filtrados:   Math.round(depositosNegFiltrados * 100) / 100,
          flujo_bancario_total:      Math.round(flujoBancarioTotal * 100) / 100,
          cantidad_movimientos:      allMovs.length,
        };
      } catch (e) { /* noop */ }
    },
    debouncedFetch() {
      clearTimeout(this._debounce);
      this._debounce = setTimeout(() => this.fetch(), 300);
    },
    go(p) { this.filters.page = p; this.fetch(); },
    openNew() {
      this.modal = { open: true, id: null, saving: false, error: '', data: emptyMov() };
    },
    // Devuelve categorías agrupadas por tipo, priorizando primero el tipo
    // del movimiento. Permite asignar una categoría de otro tipo si hace falta.
    categoriasGroup(tipoMov) {
      const map = { gasto: 'gasto', ingreso: 'ingreso', deposito_propio: 'transferencia' };
      const propio = map[tipoMov] || 'gasto';
      const labels = { gasto: 'Gastos', ingreso: 'Ingresos', transferencia: 'Transferencias' };
      const groups = [];
      // primero el grupo propio
      const propias = this.categoriasPorTipo[propio] || [];
      if (propias.length) groups.push({ label: labels[propio], items: propias });
      // después los otros
      ['gasto', 'ingreso', 'transferencia'].forEach(t => {
        if (t === propio) return;
        const arr = this.categoriasPorTipo[t] || [];
        if (arr.length) groups.push({ label: labels[t], items: arr });
      });
      return groups;
    },
    async onCategoriaInline(m, value) {
      if (value === '__new__') {
        // Abrir modal de nueva categoría, default al tipo del movimiento
        const tipoMap = { gasto: 'gasto', ingreso: 'ingreso', deposito_propio: 'transferencia' };
        this.newCat = {
          open: true, nombre: '', tipo: tipoMap[m.tipo] || 'gasto',
          color: '#6b7280', saving: false, error: '', pendingMov: m,
        };
        // Restaurar el select al valor previo mientras tanto
        await this.$nextTick();
        return;
      }
      const nuevaCatId = value === '' ? null : Number(value);
      const prev = m.categoria_id;
      m.categoria_id = nuevaCatId;
      m.categoria = nuevaCatId ? this.categorias.find(c => c.id === nuevaCatId) : null;
      try {
        await api.put(`/gastos/movimientos/${m.id}`, { categoria_id: nuevaCatId });
      } catch (e) {
        m.categoria_id = prev;
        m.categoria = prev ? this.categorias.find(c => c.id === prev) : null;
        this.$msg.error('No se pudo actualizar la categoría.');
      }
    },
    async onIneludibleInline(m, checked) {
      const prev = !!m.ineludible;
      m.ineludible = checked;
      try {
        await api.put(`/gastos/movimientos/${m.id}`, { ineludible: checked });
      } catch (e) {
        m.ineludible = prev;
        this.$msg.error('No se pudo actualizar el flag.');
      }
    },
    async onColorCat(c, color) {
      const prev = c.color;
      c.color = color;
      try {
        await api.put(`/gastos/categorias/${c.id}`, { nombre: c.nombre, tipo: c.tipo, color });
        // Refrescar la lista de movs para que el chip de categoría tome el color nuevo
        await this.fetch();
      } catch (e) {
        c.color = prev;
        this.$msg.error('No se pudo cambiar el color.');
      }
    },
    async onNombreCat(c, nombre) {
      const prev = c.nombre;
      const nombreLimpio = (nombre || '').trim();
      if (! nombreLimpio || nombreLimpio === prev) { c.nombre = prev; return; }
      c.nombre = nombreLimpio;
      try {
        await api.put(`/gastos/categorias/${c.id}`, { nombre: nombreLimpio, tipo: c.tipo, color: c.color });
        await this.fetch();
      } catch (e) {
        c.nombre = prev;
        this.$msg.error(e?.response?.data?.message || 'No se pudo renombrar.');
      }
    },
    async eliminarCat(c) {
      const ok = await this.$msg.confirm(`¿Eliminar la categoría "${c.nombre}"? Los movimientos que la usan quedan sin clasificar.`, 'Eliminar categoría');
      if (! ok) return;
      try {
        await api.delete(`/gastos/categorias/${c.id}`);
        this.categorias = this.categorias.filter(x => x.id !== c.id);
        await this.fetch();
      } catch (e) {
        this.$msg.error(e?.response?.data?.message || 'No se pudo eliminar.');
      }
    },
    abrirNuevaCategoria() {
      this.newCat = {
        open: true, nombre: '', tipo: 'gasto',
        color: '#6b7280', saving: false, error: '', pendingMov: null,
      };
    },
    async crearCategoriaRapida() {
      this.newCat.saving = true; this.newCat.error = '';
      try {
        const { data } = await api.post('/gastos/categorias', {
          nombre: this.newCat.nombre,
          tipo:   this.newCat.tipo,
          color:  this.newCat.color,
        });
        this.categorias.push(data);
        // Auto-asignar al mov pendiente
        if (this.newCat.pendingMov) {
          await this.onCategoriaInline(this.newCat.pendingMov, String(data.id));
        }
        this.newCat.open = false;
      } catch (e) {
        this.newCat.error = e?.response?.data?.message || 'No se pudo crear.';
      } finally {
        this.newCat.saving = false;
      }
    },
    onImportarPdf() {
      this.importar = {
        open: true, paso: 'upload', filename: '', cuenta: '',
        saldoInicial: null, saldoFinal: null, aplicarSaldo: true,
        fechaDesde: '', fechaHasta: '', movs: [], skipInternos: 0, error: '',
        resultado: { insertados: 0, duplicados: 0 },
      };
    },
    async onFileSelected(ev) {
      const file = ev.target.files?.[0];
      if (! file) return;

      this.importar.error = '';
      this.importar.filename = file.name;

      if (! /\.(xlsx|xls|pdf)$/i.test(file.name)) {
        this.importar.error = 'Formato no soportado. Subí un .xlsx o .pdf de Galicia.';
        return;
      }

      // Intento 1: parsing server-side (camino preferido — inmune al cache de browser).
      let backendOk = false;
      try {
        const form = new FormData();
        form.append('archivo', file);
        const { data } = await api.post('/gastos/importaciones/preview', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.applyPreviewData(data);
        backendOk = true;
      } catch (err) {
        const msg = err?.response?.data?.message || '';
        const errors = err?.response?.data?.errors || {};
        // Si el backend viejo todavía está vivo, va a quejarse del campo 'pdf' obsoleto.
        // En ese caso pasamos al fallback client-side.
        const backendIsOld = msg.toLowerCase().includes('pdf field') || errors.pdf;
        if (! backendIsOld && /\.pdf$/i.test(file.name)) {
          // PDF requiere el backend nuevo (pdftotext). No tenemos fallback.
          this.importar.error = msg || 'No pude leer el PDF. El backend nuevo todavía no está activo.';
          return;
        }
        // Fallback solo para XLSX: parseamos en el browser.
        // eslint-disable-next-line no-console
        console.warn('[gastos] backend /preview no listo, usando fallback SheetJS', msg);
      }
      if (backendOk) return;

      // Intento 2 (fallback): parsing client-side con SheetJS (solo .xlsx).
      if (! /\.(xlsx|xls)$/i.test(file.name)) return;
      try {
        const buf = await file.arrayBuffer();
        const wb  = XLSX.read(buf, { type: 'array' });
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false });

        const headerText = (rows[1] || [''])[0] || '';
        const mCuenta = headerText.match(/(\d{7})/);
        const cuenta = mCuenta ? mCuenta[1] : '';

        const movsRaw = [];
        const rawSaldos = [];
        for (let i = 6; i < rows.length; i++) {
          const r = rows[i] || [];
          const fecha = String(r[0] || '');
          if (!/^\d{2}\/\d{2}\/\d{4}$/.test(fecha)) continue;
          const desc = String(r[1] || '').trim().replace(/\s*\n\s*/g, ' | ');
          const debito = parseMontoAr(String(r[2] ?? '0'));
          const credito = parseMontoAr(String(r[3] ?? '0'));
          const monto = debito !== 0 ? debito : credito;
          const saldo = parseMontoAr(String(r[4] ?? '0'));
          if (monto === 0) continue;
          rawSaldos.push({ saldo, monto });
          movsRaw.push({
            fecha: fechaArToIso(fecha),
            descripcion: desc.slice(0, 300),
            monto,
            tipo: sugerirTipoCliente(desc, monto),
            raw_text: desc,
          });
        }

        let saldoFinal = null, saldoInicial = null;
        if (rawSaldos.length) {
          saldoFinal = rawSaldos[0].saldo;
          const ult = rawSaldos[rawSaldos.length - 1];
          saldoInicial = Math.round((ult.saldo - ult.monto) * 100) / 100;
        }

        this.applyPreviewData({
          numero_cuenta: cuenta,
          saldo_inicial: saldoInicial,
          saldo_final:   saldoFinal,
          movimientos:   movsRaw,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error en fallback SheetJS', err);
        this.importar.error = 'No pude leer el archivo ni vía backend ni vía SheetJS.';
      }
    },
    // Recibe el shape uniforme { numero_cuenta, saldo_inicial, saldo_final, movimientos }
    // de cualquiera de los dos caminos (backend o SheetJS fallback) y lo aplica al state.
    applyPreviewData(data) {
      const movsBackend = data.movimientos || [];
      const movs = [];
      let skipInternos = 0;
      for (const m of movsBackend) {
        if (esMovInterno(m.descripcion || '')) { skipInternos++; continue; }
        movs.push({
          fecha: m.fecha,
          descripcion: (m.descripcion || '').slice(0, 300),
          monto: Number(m.monto || 0),
          tipo: m.tipo || (Number(m.monto || 0) < 0 ? 'gasto' : 'ingreso'),
          raw_text: m.raw_text || m.descripcion || '',
        });
      }

      this.importar.cuenta       = data.numero_cuenta || '';
      this.importar.saldoInicial = data.saldo_inicial != null ? Number(data.saldo_inicial) : null;
      this.importar.saldoFinal   = data.saldo_final != null ? Number(data.saldo_final) : null;
      this.importar.skipInternos = skipInternos;

      if (! movs.length) {
        this.importar.error = 'No detecté movimientos en el archivo. ¿Es un extracto Galicia?';
        return;
      }

      const fechas = movs.map(m => m.fecha).sort();
      this.importar.fechaDesde = fechas[0];
      this.importar.fechaHasta = fechas[fechas.length - 1];
      this.importar.movs = movs;
      this.importar.paso = 'preview';
    },
    async confirmarImportacion() {
      this.importar.paso = 'saving';
      this.importar.error = '';
      try {
        // Si el owner destildó "Aplicar saldo", no mandamos saldo ni cuenta:
        // solo se guardan los movimientos, el saldo de la cuenta queda como estaba.
        const incluirSaldo = !!this.importar.aplicarSaldo && this.importar.saldoFinal != null;
        const payload = {
          banco: 'galicia',
          cuenta_tipo: 'ca_ars',
          numero_cuenta: incluirSaldo ? (this.importar.cuenta || null) : null,
          saldo_inicial: incluirSaldo ? this.importar.saldoInicial : null,
          saldo_final:   incluirSaldo ? this.importar.saldoFinal   : null,
          archivo: this.importar.filename,
          movimientos: this.importar.movs.map(m => ({
            fecha: m.fecha,
            descripcion: m.descripcion,
            monto: m.monto,
            tipo: m.tipo,
            categoria_id: null,
            ineludible: false,
            raw_text: m.raw_text,
          })),
        };
        const { data } = await api.post('/gastos/importaciones/confirmar', payload);
        this.importar.resultado = {
          insertados: data.insertados || 0,
          duplicados: data.duplicados || 0,
        };
        this.importar.paso = 'done';
        // Refrescar dashboard
        await Promise.all([this.fetch(), this.fetchResumen(), this.fetchSaldos()]);
      } catch (e) {
        this.importar.paso = 'preview';
        this.importar.error = e?.response?.data?.message || 'Error al guardar la importación.';
      }
    },
    openEdit(m) {
      this.modal = {
        open: true, id: m.id, saving: false, error: '',
        data: {
          fecha: String(m.fecha).slice(0, 10),
          descripcion: m.descripcion,
          monto: Number(m.monto),
          tipo: m.tipo,
          categoria_id: m.categoria_id,
          ineludible: !!m.ineludible,
        }
      };
    },
    async save() {
      this.modal.saving = true; this.modal.error = '';
      try {
        if (this.modal.id) {
          await api.put(`/gastos/movimientos/${this.modal.id}`, this.modal.data);
        } else {
          await api.post('/gastos/movimientos', this.modal.data);
        }
        this.modal.open = false;
        await Promise.all([this.fetch(), this.fetchResumen()]);
      } catch (e) {
        this.modal.error = e?.response?.data?.message || 'No se pudo guardar.';
      } finally { this.modal.saving = false; }
    },
    async del() {
      const ok = await this.$msg.confirm('¿Eliminar este movimiento?', 'Eliminar');
      if (!ok) return;
      try {
        await api.delete(`/gastos/movimientos/${this.modal.id}`);
        this.modal.open = false;
        await Promise.all([this.fetch(), this.fetchResumen()]);
      } catch (e) {
        this.modal.error = e?.response?.data?.message || 'No se pudo eliminar.';
      }
    },
    tipoLabel(t) {
      return { gasto: 'Gasto', ingreso: 'Ingreso', deposito_propio: 'Depósito propio' }[t] || t;
    },
    tipoPill(t) {
      return { gasto: 'pill-r', ingreso: 'pill-g', deposito_propio: 'pill-b' }[t] || 'pill-b';
    },
    fmt(n) {
      return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(Number(n || 0));
    },
    fmtDate(d) {
      if (!d) return '—';
      const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${parseInt(m[3], 10)}/${parseInt(m[2], 10)}/${m[1]}`;
      return new Date(d).toLocaleDateString('es-AR');
    }
  }
};
</script>

<style scoped>
/* Select inline: pequeño, encaja en celda de tabla */
.inline-select {
  font-size: 12.5px;
  padding: 4px 8px;
  height: 30px;
  max-width: 100%;
  cursor: pointer;
}

/* Cards en mobile, tabla en desktop. Breakpoint: 900px */
.gastos-cards { display: none; }
.gastos-tabla { display: block; }

@media (max-width: 900px) {
  .gastos-tabla { display: none; }
  .gastos-cards { display: block; padding: 4px 0; }

  .gasto-card {
    border-bottom: 1px solid var(--brd, #e2e8f0);
    padding: 12px 12px;
    background: #fff;
  }
  .gasto-card:last-child { border-bottom: none; }
  .gasto-card__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 10px;
  }
  .gasto-card__head > div:first-child {
    flex: 1;
    min-width: 0;
    word-break: break-word;
  }
  .gasto-card__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 8px;
    flex-wrap: wrap;
  }
}
</style>
